import {
	Autocomplete,
	Button,
	Loading,
	Table,
	type TableAction,
	Title,
} from 'src/components';
import { type ChangeEvent, useEffect, useState } from 'react';
import {
	CircularProgress,
	FormControl,
	FormLabel,
	Grid,
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { type ICourse, type IInstitution } from 'src/models';
import { type RootState, history } from 'src/store';
import { classNames } from 'react-mde/lib/definitions/util/ClassNames';
import { isDefaultDate } from 'src/helpers';
import { useDispatch, useSelector } from 'react-redux';
import CalendarIcon from '@material-ui/icons/Event';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import MomentUtils from '@date-io/moment';
import courseActions from 'src/store/Courses/actions';
import institutionActions from 'src/store/Institutions/actions';
import moment from 'moment';
import userActions from 'src/store/Users/actions';

const columns = [
	{
		key: 'user',
		label: 'Nombre',
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[22:8]
		render: (value: any, row: any) => value,
	},
	{
		key: 'qualification',
		label: 'Calificación',
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[22:8]
		render: (value: any, row: any) => value,
	},
	{
		key: 'attempt',
		label: 'Intento',
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[22:8]
		render: (value: any, row: any) => value,
	},
	{
		key: 'date',
		label: 'Fecha',
		render: (value: any, row: any) =>
			isDefaultDate(value) ? '' : moment(value).format('DD/MM/YYYY'),
	},
];

function Reviews() {
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();
	const institutions = useSelector(
		(state: RootState) => state.institutions.institutions,
	);
	const { courses, isDownloadingBulk, review } = useSelector(
		(state: RootState) => state.courses,
	);
	const { course, institution } = review;
	const {
		data: certificatesData,
		isLoading,
		metadata,
		page,
		pageSize,
	} = useSelector((state: RootState) => state.users.certificates);
	const [date, setDate] = useState<Date>(new Date());

	const cleanData = () =>
		dispatch(
			userActions.setCertificates([], { totalPages: 0, totalResults: 0 }),
		);
	const setInstitution = (institution: IInstitution) =>
		dispatch(courseActions.setReviewInstitution(institution));
	const setCourse = (course: ICourse) =>
		dispatch(courseActions.setReviewCourse(course));

	// NOTE:
	// ./src/components/Reports/AdvancesDialog.tsx[39:5]

	function handleInstitutionChange(institution: IInstitution) {
		if (institution?.id) {
			setInstitution(institution);
			dispatch(courseActions.getByInstitution(institution.id));
		} else {
			setInstitution({});
		}

		setCourse({});
		cleanData();
	}

	function handleCourseChange(course: ICourse) {
		if (course?.id) {
			setCourse(course);
			dispatch(
				userActions.getCertificates(
					institution.id!,
					course.id,
					moment(date).year(),
					1,
					pageSize,
				),
			);
		} else {
			cleanData();
		}
	}

	const actions: TableAction[] = [
		{
			disabled: (row: any) => (row.attempt || 0) === 0,
			label: 'Ver Examen',
			onClick: (row: any) => {
				history.push(`/app/reviews/${row.courseId}/${row.userId}`);
			},
		},
	];

	useEffect(() => {
		if (institution.id && course.id)
			dispatch(
				userActions.getCertificates(
					institution.id,
					course.id,
					moment(date).year(),
					1,
					pageSize,
				),
			);
		else dispatch(institutionActions.getAll());
	}, []);

	return (
		<Grid container>
			<Grid item xs={6}>
				<Title
					breadcrumbs={[{ label: 'Revisión' }]}
					title={'Revisión'}
				/>
			</Grid>

			<Grid
				item
				style={{
					alignItems: 'flex-end',
					display: 'flex',
					justifyContent: 'flex-end',
				}}
				xs={6}
			>
				<Button
					backgroundColor={'white'}
					disabled={
						!institution?.id || !course.id || isDownloadingBulk
					}
					onClick={() =>
						dispatch(
							courseActions.downloadAllUsersExam(
								institution.id!,
								course.id!,
								moment(date).year(),
							),
						)
					}
					startIcon={<DownloadIcon />}
				>
					{isDownloadingBulk ? (
						<CircularProgress size={26} />
					) : (
						'Descargar exámenes'
					)}
				</Button>
			</Grid>

			<Grid container spacing={2} style={{ marginTop: 24 }}>
				<Grid item md={5} xs={12}>
					<Autocomplete
						getOptionLabel={(option: any) => option.name}
						label={'Institución'}
						onChange={(e: ChangeEvent<any>, values: any) => {
							handleInstitutionChange(values);
						}}
						options={institutions}
						value={institution}
					/>
				</Grid>

				<Grid item md={5} xs={12}>
					<Autocomplete
						getOptionLabel={(option: any) => option.name}
						label={'Curso'}
						onChange={(e: ChangeEvent<any>, values: any) => {
							handleCourseChange(values);
						}}
						options={courses}
						value={course}
					/>
				</Grid>

				<Grid item md={2} xs={12}>
					<FormControl fullWidth>
						<FormLabel> Año </FormLabel>

						<MuiPickersUtilsProvider
							locale={'es'}
							utils={MomentUtils}
						>
							<DatePicker
								InputProps={{
									endAdornment: <CalendarIcon />,
								}}
								autoOk
								format={'YYYY'}
								inputVariant={'outlined'}
								onChange={(date: any) => {
									setDate(date);

									if (institution.id && course.id) {
										dispatch(
											userActions.getCertificates(
												institution.id,
												course.id,
												moment(date).year(),
												1,
												pageSize,
											),
										);
									}
								}}
								value={date}
								views={['year']}
							/>
						</MuiPickersUtilsProvider>
					</FormControl>
				</Grid>

				<Grid item xs={12}>
					<Loading isLoading={isLoading}>
						<Table
							actions={actions}
							columns={columns}
							data={certificatesData}
							handlePagination={(event: any, newPage: number) => {
								dispatch(
									userActions.getCertificates(
										institution.id!,
										course.id!,
										moment(date).year(),
										newPage,
										pageSize,
									),
								);
							}}
							metadata={metadata}
							page={page}
						/>
					</Loading>
				</Grid>
			</Grid>
		</Grid>
	);
}

export default Reviews;
